import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  styled,
} from "@mui/material";
import { Link } from "react-router-dom";

// Styled components
const GradientTypography = styled(Typography)(({ theme }) => ({
  background: "linear-gradient(90deg, #FF6F61 0%, #FFC371 100%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
  display: "inline",
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(90deg, #FF6F61 0%, #FFC371 100%)",
  color: "white",
  borderRadius: "50px",
  padding: theme.spacing(1, 3),
  textTransform: "none",
  fontWeight: 600,
  "&:hover": {
    background: "linear-gradient(90deg, #FF6F61 0%, #FFC371 100%)",
    opacity: 0.9,
    boxShadow: theme.shadows[4],
  },
}));

const OutlineButton = styled(Button)(({ theme }) => ({
  borderRadius: "50px",
  border: "1px solid #FFB347",
  color: theme.palette.text.primary,
  padding: theme.spacing(1, 3),
  textTransform: "none",
  fontWeight: 600,
  "&:hover": {
    backgroundColor: "rgba(255, 179, 71, 0.05)",
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[2],
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: theme.shadows[6],
  },
}));

const FloatingBox = styled(Box)(({ theme }) => ({
  "@keyframes float": {
    "0%, 100%": { transform: "translateY(0px)" },
    "50%": { transform: "translateY(-10px)" },
  },
  animation: "float 6s ease-in-out infinite",
}));

// NOTE: Changed from width: '100%' to maxWidth: '80%', margin: '0 auto', display: 'block'
// so images remain centered within their parent.
const AppImage = styled("img")(({ theme }) => ({
  maxWidth: "80%",
  display: "block",
  margin: "0 auto",
  borderRadius: theme.spacing(3),
  boxShadow: theme.shadows[4],
}));

const HoverImage = styled("img")(({ theme, height = 48 }) => ({
  height: height,
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
  },
}));

const GlowagainApp = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      sx={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        background: "linear-gradient(135deg, #F5F5F5 0%, #FFFFFF 100%)",
        color: "#1a1a1a",
        minHeight: "100vh",
      }}
    >
      {/* Navigation */}
      <AppBar
        position="fixed"
        elevation={2} // Subtle shadow for modern depth
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.95)", // Light, translucent navbar
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid rgba(255, 111, 97, 0.1)",
        }}
      >
        <Toolbar
          sx={{
            maxWidth: { sm: "100%", md: "1200px" },
            width: "100%",
            mx: "auto",
          }}
        >
          <Typography variant="h5" fontWeight="bold" sx={{ flexGrow: 1 }}>
            <GradientTypography variant="inherit">Glowagain</GradientTypography>
          </Typography>

          {!isMobile && (
            <Box sx={{ display: "flex", gap: 4, mr: 4 }}>
              <Typography
                component="a"
                href="#how-it-works"
                sx={{
                  color: "text.primary",
                  textDecoration: "none",
                  "&:hover": { opacity: 0.8 },
                }}
              >
                How It Works
              </Typography>
              <Typography
                component="a"
                href="#testimonials"
                sx={{
                  color: "text.primary",
                  textDecoration: "none",
                  "&:hover": { opacity: 0.8 },
                }}
              >
                Stories
              </Typography>
            </Box>
          )}

          <GradientButton href="#download" disableElevation>
            Get the App
          </GradientButton>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Container
        component="section"
        maxWidth="md"
        sx={{
          pt: { xs: 16, sm: 20, md: 24 },
          pb: { xs: 10, md: 16 },
          textAlign: "center",
        }}
      >
        <Typography
          variant={isMobile ? "h4" : "h3"}
          component="h1"
          fontWeight="bold"
          sx={{ mb: 3, lineHeight: 1.2 }}
        >
          Ready to{" "}
          <GradientTypography variant="inherit">shine</GradientTypography> after
          heartbreak?
        </Typography>

        <Typography
          variant={isMobile ? "body1" : "h6"}
          color="text.secondary"
          sx={{ mb: 5, maxWidth: "85%", mx: "auto" }}
        >
          Glowagain helps you rebuild confidence, recognize patterns, and feel
          completely ready before your next relationship.
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "center",
            gap: 2,
            mb: 6,
            width: "100%",
            "& > a": {
              width: { xs: "100%", sm: "auto" },
            },
          }}
        >
          <GradientButton href="#download" size="large" disableElevation>
            Start Your Glow Journey
          </GradientButton>
          <OutlineButton href="#how-it-works" size="large" disableElevation>
            How It Works
          </OutlineButton>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Available on iOS & Android
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
          <Link to="/early-access">
            <HoverImage src="appstore.webp" alt="App Store" height={48} />
          </Link>
          <Link to="/early-access">
            <HoverImage src="playstore.webp" alt="Google Play" height={48} />
          </Link>
        </Box>
      </Container>

      {/* App Screens Section */}
      <Box component="section" sx={{ py: { xs: 8, md: 12 }, bgcolor: "white" }}>
        <Container maxWidth="lg" sx={{ textAlign: "center" }}>
          <Grid
            container
            spacing={4}
            alignItems="center"
            justifyContent="center"
          >
            {/* Center each image by making the Grid item a flex container */}
            {/* @ts-expect-error */}
            <Grid
              item
              xs={12}
              md={5}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <FloatingBox>
                <AppImage
                  src="/assets/screen1.png"
                  alt="Glowagain App Screen 1"
                />
              </FloatingBox>
            </Grid>
            {/* @ts-expect-error */}
            <Grid
              item
              xs={12}
              md={5}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <FloatingBox sx={{ animationDelay: "1s" }}>
                <AppImage
                  src="/assets/screen2.png"
                  alt="Glowagain App Screen 2"
                />
              </FloatingBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 30-Day Snapshot Section */}
      <Box
        id="how-it-works"
        component="section"
        sx={{
          py: { xs: 8, md: 12 },
          background: "linear-gradient(to bottom, white, #FFF5E1)",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 8 } }}>
            <Typography
              variant={isMobile ? "h4" : "h3"}
              fontWeight="bold"
              mb={2}
            >
              A{" "}
              <GradientTypography variant="inherit">30-Day</GradientTypography>{" "}
              Reset Plan That Works
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{
                mb: 3,
                maxWidth: "800px",
                mx: "auto",
                fontSize: { xs: "1rem", md: "1.25rem" },
              }}
            >
              Structured recovery from Day 1 to Day 30 — designed by
              relationship coaches and guided by AI.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {[
              {
                title: "Days 1–10",
                subtitle: "Rediscover your sense of self.",
                items: [
                  "Confidence exercises",
                  "Identity reflection",
                  "Values journaling",
                ],
              },
              {
                title: "Days 11–20",
                subtitle: "Break emotional patterns.",
                items: [
                  "Attachment awareness",
                  "Red flag training",
                  "Emotional rewiring",
                ],
              },
              {
                title: "Days 21–30",
                subtitle: "Prepare to date again.",
                items: [
                  "Dating mindset reset",
                  "Profile optimization",
                  "Readiness checklist",
                ],
              },
            ].map((phase, index) => (
              // Use md={4} so 3 cards fill 12 columns and remain perfectly centered
              //@ts-expect-error
              <Grid component="div" item xs={12} sm={6} md={4} key={index}>
                <StyledCard>
                  <CardContent sx={{ p: 3, textAlign: "center" }}>
                    <GradientTypography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ display: "block", mb: 1 }}
                    >
                      {phase.title}
                    </GradientTypography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {phase.subtitle}
                    </Typography>
                    <List disablePadding>
                      {phase.items.map((item, i) => (
                        <ListItem
                          key={i}
                          disableGutters
                          sx={{ py: 0.5, justifyContent: "center" }}
                        >
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            {/* <FiberManualRecordIcon sx={{ fontSize: 8, color: '#FF6F61' }} /> */}
                          </ListItemIcon>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{
                              variant: "body2",
                              color: "text.secondary",
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials */}
      <Box
        id="testimonials"
        component="section"
        sx={{
          py: { xs: 8, md: 12 },
          background: "linear-gradient(to bottom, white, #FFF5E1)",
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant={isMobile ? "h4" : "h3"}
            fontWeight="bold"
            sx={{ mb: { xs: 4, md: 6 }, textAlign: "center" }}
          >
            What Users Are Saying
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {[
              {
                quote:
                  "This app genuinely helped me understand my past and gave me tools to approach dating confidently again.",
                author: "— Alex, 28",
              },
              {
                quote:
                  "I didn't realize how much I needed Glowagain until I started using it. 10/10 for reflection prompts.",
                author: "— Tasha, 32",
              },
              {
                quote:
                  "It's like a therapist, tracker, and cheerleader in your pocket. A must-have after any breakup.",
                author: "— Jordan, 26",
              },
            ].map((testimonial, index) => (
              // Again, md={4} so three testimonials fill up one row nicely
              //@ts-expect-error
              <Grid component="div" item xs={12} sm={6} md={4} key={index}>
                <StyledCard sx={{ p: 1 }}>
                  <CardContent sx={{ p: 3, textAlign: "center" }}>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      "{testimonial.quote}"
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {testimonial.author}
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* TikTok Embed Section */}
      <Box
        component="section"
        sx={{
          py: { xs: 8, md: 12 },
          background: "linear-gradient(to bottom, #fff, #FFF5E1)",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant={isMobile ? "h4" : "h3"}
            fontWeight="bold"
            sx={{ mb: 4 }}
          >
            Glowagain on TikTok
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mb: 6, fontSize: { xs: "1rem", md: "1.25rem" } }}
          >
            See glow-up stories, self-love tips, and community highlights from
            users just like you.
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {[
              "https://www.tiktok.com/embed/7343658494593445125",
              "https://www.tiktok.com/embed/7343628394928379136",
            ].map((url, index) => (
              //@ts-expect-error
              <Grid item xs={12} sm={6} key={index}>
                <Box
                  component="iframe"
                  src={url}
                  sx={{
                    width: "100%",
                    height: 600,
                    border: "none",
                    borderRadius: 2,
                    boxShadow: 3,
                  }}
                  allow="autoplay; encrypted-media"
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box
        id="download"
        component="section"
        sx={{
          py: { xs: 8, md: 12 },
          textAlign: "center",
          background: "linear-gradient(to bottom, #FFF5E1, white)",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant={isMobile ? "h4" : "h3"}
            fontWeight="bold"
            sx={{ mb: 2 }}
          >
            Begin Your Glow Journey
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              mb: 5,
              fontSize: { xs: "1rem", md: "1.25rem" },
            }}
          >
            Download Glowagain and start rebuilding the most important
            relationship — the one with yourself.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
            <Link to="/early-access">
              <HoverImage src="appstore.webp" alt="App Store" height={56} />
            </Link>
            <Link to="/early-access">
              <HoverImage src="playstore.webp" alt="Google Play" height={56} />
            </Link>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          bgcolor: "#2C2C3C",
          py: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="body2" color="#FFF5E1">
          &copy; 2025 Glowagain. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default GlowagainApp;
